import React, { useRef, useState } from 'react';
import styles from './signaturePad.module.css';

interface SignaturePadProps {
  handleChange: (newSignature: string) => void;
}

export const SignaturePad = ({ handleChange }: SignaturePadProps): React.JSX.Element => {
  const containingDiv = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  const getCanvasCoords = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (showPlaceholder) {
      setShowPlaceholder(false);
      // Remove placeholder by redrawing the white background
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    isDrawingRef.current = true;
    canvas.setPointerCapture(event.pointerId);
    lastPosRef.current = getCanvasCoords(event);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const currentPos = getCanvasCoords(event);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(currentPos.x, currentPos.y);
    ctx.stroke();
    lastPosRef.current = currentPos;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    // don't update signature if pen hasn't been down yet
    if (showPlaceholder) {
      return;
    }
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.releasePointerCapture(event.pointerId);
      const dataURL = canvas.toDataURL('image/png');
      handleChange(dataURL);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#FFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    setShowPlaceholder(true);
    handleChange('');
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create a temporary canvas with the same dimensions.
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    // Fill with white background.
    tempCtx.fillStyle = '#FFF';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Draw the current signature on top.
    tempCtx.drawImage(canvas, 0, 0);

    // Generate a PNG image and download it.
    const imageURL = tempCanvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = imageURL;
    a.download = 'signature.png';
    a.click();
  };

  return (
    <div ref={containingDiv} className={styles.SignaturePad}>
      <canvas
        ref={canvasRef}
        style={{ touchAction: 'none' }}
        width={containingDiv.current?.clientWidth}
        height={200}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      {showPlaceholder && (
        <div className={styles.SignaturePadPlaceholder}>
          <p>
            <img src="/assets/icons/pencil.svg" />
            Host's signature
          </p>
        </div>
      )}
      <div className={styles.SignaturePadButtons}>
        <div onClick={clearCanvas} className={styles.ClearButton}>
          <img src="/assets/icons/envelope.svg" />
          <p>Clear</p>
        </div>
        <div onClick={handleDownload} className={styles.DownloadButton}>
          <img src="/assets/icons/chat.svg" />
          <p>Download</p>
        </div>
      </div>
    </div>
  );
};
